/* @format @flow */

import { Map } from 'immutable';
import { connect } from 'react-redux';
import F from 'lodash/fp';
import React from 'react';

import { Button, Footer, Page, PageContainer, Panel } from '@sonyged/ext-proc';

import type { ContestType, StoreState } from '../../redux/types';
import { type Rank, type RankingForCourseContent, type Scoreboard } from '../../redux/reducer/ranking';
import { contest } from '../../redux/selector';
import { makeT, themeOf } from './utils';
import IceTrophy from '../../../assets/images/pages/ice/fields/fi_ranking_ice-trophy@2x.png';
import PizzaTrophy from '../../../assets/images/pages/pizza/fields/fi_ranking_piza-trophy@2x.png';
import SushiTrophy from '../../../assets/images/pages/sushi/fi_ranking_sushi-trophy@2x.png';

const dict = {
  _: {
    trophySrc: IceTrophy,
    title: 'あなたの さいこうきろく',
    yen: 'えん',
    profit: 'もらった おかね',
    person: 'にんずう',
    thisMonth: 'こんげつ',
    japanFiscalYear: 'いままで',
  },
  pizza: {
    trophySrc: PizzaTrophy,
    yen: '円',
    profit: 'もらったお金',
  },
  sushi: {
    trophySrc: SushiTrophy,
    title: 'あなたのさいこうきろく',
    yen: '円',
    profit: 'もらったお金',
    person: '人数',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
  },
  'ice-a': {
    trophySrc: IceTrophy,
    title: 'あなたのさいこうきろく',
    yen: '円',
    profit: 'もうけたお金',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
    person: '人数',
  },
  'pizza-a': {
    trophySrc: PizzaTrophy,
    title: 'あなたの最高記録',
    yen: '円',
    profit: '売り上げ',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
    person: '人数',
  },
  'sushi-a': {
    trophySrc: SushiTrophy,
    title: 'あなたの最高記録',
    yen: '円',
    profit: 'もうけたお金',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
    person: '人数',
  },
};
const t = makeT(dict);

type RankingItemProps = {
  rank: Rank,
  kind: ContestType,
  focused: boolean,
};

const RankingItem = ({ rank, kind, focused }: RankingItemProps) => {
  const makeBadge = (kind: ContestType, type: 'gold' | 'silver' | 'bronze') => {
    return (
      <i className={`icon-proc-${themeOf(kind)}-${type}`}>
        <i className="path1 icon-proc-path" />
        <i className="path2 icon-proc-path" />
        <i className="path3 icon-proc-path" />
        <i className="path4 icon-proc-path" />
        <i className="path5 icon-proc-path" />
      </i>
    );
  };

  let badge = null;
  switch (rank.position) {
    case 1:
      badge = makeBadge(kind, 'gold');
      break;
    case 2:
      badge = makeBadge(kind, 'silver');
      break;
    case 3:
      badge = makeBadge(kind, 'bronze');
      break;
  }

  return (
    <tr className={`proc-scoreBoard-rankItem ${focused ? 'proc-scoreBoard-rankItem--focused' : ''}`}>
      <td />
      <td className="proc-scoreBoard-badge">{badge}</td>
      <td className="proc-scoreBoard-position">{rank.position}.</td>
      {typeof rank.point === 'undefined' ? (
        <td className="proc-scoreBoard-amount proc-scoreBoard-amount--blank" />
      ) : (
        <td className="proc-scoreBoard-amount">
          {rank.point}
          &nbsp;
          <span className="proc-scoreBoard-yen">{t(kind, 'yen')}</span>
        </td>
      )}
      <td className="proc-scoreBoard-peopleCount">{rank.numberOfPeople || 0}</td>
      <td />
    </tr>
  );
};

const OutOfChartRankingItem = ({ rank, kind }: { rank: Rank, kind: ContestType }) => {
  return (
    <tr className="proc-scoreBoard-rankItem proc-scoreBoard-rankItem--focused">
      <td />
      <td colSpan={2} className="proc-scoreBoard-you">
        あなた
      </td>
      {typeof rank.point === 'undefined' ? (
        <td className="proc-scoreBoard-amount proc-scoreBoard-amount--blank" />
      ) : (
        <td className="proc-scoreBoard-amount">
          {rank.point}
          &nbsp;
          <span className="proc-scoreBoard-yen">{t(kind, 'yen')}</span>
        </td>
      )}
      <td />
      <td />
    </tr>
  );
};

const SeparatorRankingItem = () => (
  <tr className="proc-scoreBoard-rankItem proc-scoreBoard-rankItem--separator">
    <td />
    <td colSpan={4} />
    <td />
  </tr>
);

const EllipsisRankingItem = () => (
  <tr className="proc-scoreBoard-rankItem proc-scoreBoard-rankItem--ellipsis">
    <td />
    <td colSpan={4}>
      <span className="proc-rankItem-ellipsis">...</span>
    </td>
    <td />
  </tr>
);

const GapRankingItem = () => (
  <tr className="proc-scoreBoard-rankItem proc-scoreBoard-rankItem--gap">
    <td />
    <td colSpan={4} />
    <td />
  </tr>
);

const RankingTable = ({ board: { ranks: originalRanks, me }, kind }: { board: Scoreboard, kind: ContestType }) => {
  const ranks = F.pipe(
    F.filter(rank => rank.point && rank.point > 0),
    F.map(rank => [rank.position, rank]),
    Map
  )(originalRanks);

  const rankItemFromPosition = (position: number) => {
    const rank = ranks.get(position, { position });
    return <RankingItem kind={kind} rank={rank} focused={rank.position === me} key={rank.position} />;
  };

  let body = null;

  if (me && me <= 11) {
    body = (
      <React.Fragment>
        {F.range(1, Math.max(me + 1, 11)).map(rankItemFromPosition)}
        <SeparatorRankingItem />
      </React.Fragment>
    );
  } else if (me && me < 98) {
    body = (
      <React.Fragment>
        {F.range(1, 11).map(rankItemFromPosition)}
        <EllipsisRankingItem />
        {rankItemFromPosition(me)}
        <EllipsisRankingItem />
        {rankItemFromPosition(99)}
        <SeparatorRankingItem />
      </React.Fragment>
    );
  } else if (me && me === 98) {
    body = (
      <React.Fragment>
        {F.range(1, 11).map(rankItemFromPosition)}
        <EllipsisRankingItem />
        {rankItemFromPosition(me)}
        {rankItemFromPosition(99)}
        <SeparatorRankingItem />
      </React.Fragment>
    );
  } else if (me && (me === 99 || me === 100)) {
    body = (
      <React.Fragment>
        {F.range(1, 11).map(rankItemFromPosition)}
        <EllipsisRankingItem />
        {rankItemFromPosition(me)}
        <SeparatorRankingItem />
      </React.Fragment>
    );
  } else {
    body = (
      <React.Fragment>
        {F.range(1, 11).map(rankItemFromPosition)}
        <EllipsisRankingItem />
        {rankItemFromPosition(100)}
        <SeparatorRankingItem />
        {me ? (
          <React.Fragment>
            <GapRankingItem />
            <OutOfChartRankingItem rank={ranks.get(me, { position: me })} kind={kind} />
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  }

  return (
    <table className="proc-scoreBoard-table">
      <thead>
        <tr className="proc-scoreBoard-rankHeader">
          <th />
          <th />
          <th />
          <th>{t(kind, 'profit')}</th>
          <th>{t(kind, 'person')}</th>
          <th />
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </table>
  );
};

type ScoreBoardProps = {
  boards: Array<{ name: string, board: Scoreboard }>,
  kind: ContestType,
};

type ScoreBoardState = {
  tabIndex: number,
};

export class ScoreBoard extends React.Component<ScoreBoardProps, ScoreBoardState> {
  state = { tabIndex: 0 };

  render() {
    const { kind, boards } = this.props;
    const { tabIndex } = this.state;

    return (
      <Panel.vertical className="proc-scoreBoard">
        <ul className="proc-scoreBoard-type">
          {boards.map(({ name }, i) => (
            <li
              onClick={() => {
                this.setState({ tabIndex: i });
              }}
              key={i}
              className={`proc-scoreBoard-typeItem ${i === tabIndex ? 'proc-scoreBoard-typeItem--selected' : ''}`}
            >
              {name}
            </li>
          ))}
        </ul>

        <RankingTable board={boards[tabIndex].board} kind={kind} />
      </Panel.vertical>
    );
  }
}

const Trophy = ({ kind }: { kind: ContestType }) => <img src={t(kind, 'trophySrc')} />;

type HighScorePanelProps = { highscore: number, kind: ContestType };
const HighScorePanel = ({ highscore, kind }: HighScorePanelProps) => (
  <div className="proc-highScorePanel">
    <div>
      <span className="proc-highScorePanel-title">{t(kind, 'title')}</span>
      <span className="proc-highScorePanel-score">
        {highscore}
        &nbsp;
        <span className="proc-highScorePanel-yen">{t(kind, 'yen')}</span>
      </span>
    </div>
    <div className="proc-highScorePanel-trophy">
      <Trophy kind={kind} />
    </div>
  </div>
);

type RankingPageProps = {
  boards: Array<{ name: string, board: Scoreboard }>,
  highscore?: number,
  kind: ContestType,
  onNextClick: Function,
};

export const RankingPage = ({ kind, boards, highscore, onNextClick }: RankingPageProps) => {
  if (highscore && highscore < 0) highscore = 0;
  return (
    <div className={`${themeOf(kind)} flex flex-column h100`}>
      <Page.fluid className="tertiary100-bg-color flex1">
        <PageContainer.normal fullHeight={false}>
          {highscore && highscore > 0 ? <HighScorePanel highscore={highscore} kind={kind} /> : null}
          <ScoreBoard boards={boards} kind={kind} />
        </PageContainer.normal>
      </Page.fluid>
      <Footer.normal>
        <Button.attention onClick={onNextClick}>もどる</Button.attention>
      </Footer.normal>
    </div>
  );
};

type RankingPageContainerOwnProps = {|
  kind: ContestType,
  onNextClick: Function,
|};
type RankingPageContainerProps = {
  ...RankingPageContainerOwnProps,
  courseContentUid: ?string,
  boards: RankingForCourseContent,
};

const RankingPageContainer = (props: RankingPageContainerProps) => {
  const { courseContentUid, boards, ...rest } = props;
  if (!courseContentUid) throw new Error(`Unknow ${rest.kind} uid`);

  const alltimeBoard = boards.get('japanFiscalYear');
  const alltimeRank = alltimeBoard && alltimeBoard.ranks.find(x => x.position === alltimeBoard.me);
  const monthlyBoard = boards.get('monthly');
  if (!alltimeBoard || !monthlyBoard) return null;
  return (
    <RankingPage
      {...rest}
      highscore={alltimeRank && alltimeRank.point}
      boards={[
        {
          name: t(props.kind, 'thisMonth'),
          board: monthlyBoard,
        },
        {
          name: t(props.kind, 'japanFiscalYear'),
          board: alltimeBoard,
        },
      ]}
    />
  );
};

const mapStateToProps = (state: StoreState, op: RankingPageContainerOwnProps) => {
  const courseContentUid = contest[op.kind].fieldUid(state);
  return {
    courseContentUid,
    boards: state.ranking.rankings.get(courseContentUid ? courseContentUid : ''),
  };
};

export default connect<RankingPageContainerProps, RankingPageContainerOwnProps, _, _, _, _>(
  mapStateToProps,
  null
)(RankingPageContainer);
